import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import {
    MultiStepBriefForm,
    useBriefContext,
    useBriefQuery,
    useClaimRequestMutation,
    useUpdateProductOnBriefChange,
} from '@99designs/feature-design-services-brief';
import { getApolloErrorType } from '@99designs/graph-utils';
import { __, __url, getCurrentCulture } from '@99designs/i18n';
import { useSendPageViewOnce } from '@99designs/tracking';
import { ErrorPage, NotFoundErrorPage, PermissionErrorPage } from '../Error';

export const Brief: React.FC = () => {
    const { jobId } = useParams();
    const [searchParams] = useSearchParams();

    const briefId = jobId as string;
    const locale = useMemo(() => getCurrentCulture(), []);

    useSendPageViewOnce(locale);
    const { SignIn, isSignedIn } = useIdentityContext();
    const { onCompleted, isRequestQueryLoading, isRequestClaimed } = useBriefContext();
    const [claimRequest, { error: claimRequestError }] = useClaimRequestMutation();

    const { data, loading, error } = useBriefQuery({
        variables: {
            id: briefId,
        },
        onCompleted,
        errorPolicy: 'all',
    });

    useUpdateProductOnBriefChange(data?.brief);

    const correlationId = data?.brief?.correlationId;

    const isRedirectToDraftBriefs = searchParams.get('isRedirectToDrafts') || '0';

    const customerIdentified = useCallback(async () => {
        await claimRequest({
            variables: { requestId: correlationId ?? '' },
        });

        if (['1', 'true'].includes(isRedirectToDraftBriefs.toLowerCase())) {
            const destination = __url('/graphic-design/dashboard?tab=draft');
            if (destination.length > 0) {
                window.location.href = destination;

                return null;
            }

            bugtracker.notify(new Error('could not localise graphic design dashboard URL'));
        }
    }, [claimRequest, correlationId, isRedirectToDraftBriefs]);

    // Claim the brief if the user is signed in and the request is unclaimed.
    useEffect(() => {
        if (isSignedIn && isRequestClaimed === false && correlationId) {
            void customerIdentified();
        }
    }, [isSignedIn, customerIdentified, correlationId, isRequestClaimed]);

    if (loading || isRequestQueryLoading || !data) {
        return <FullPageSpinner />;
    }

    if (claimRequestError) {
        return (
            <ErrorPage
                title={__(
                    'There was an error saving your brief. Please refresh the page or try again later.',
                    {
                        _context: 'Shown to user if there is an error saving the brief',
                    }
                )}
                error={
                    new Error(
                        `Could not claim brief ${briefId} before redirecting to dashboard: ${claimRequestError?.message}`
                    )
                }
            />
        );
    }

    if (error) {
        const errorType = getApolloErrorType(error);
        if (errorType === 'UNAUTHENTICATED') {
            if (!isSignedIn) {
                SignIn();
                return <FullPageSpinner />;
            }

            return (
                <ErrorPage
                    title={__(
                        'There was an error loading your brief. Please refresh the page or try again later.'
                    )}
                    error={
                        new Error(
                            `User is logged in on Vistaprint but has no 99designs session: ${error.message}`
                        )
                    }
                    metadata={{ briefId }}
                />
            );
        }

        if (errorType === 'PERMISSION_DENIED') {
            return <PermissionErrorPage />;
        }

        if (errorType === 'NOT_FOUND') {
            return <NotFoundErrorPage />;
        }

        if (errorType === 'UNKNOWN_ERROR') {
            return (
                <ErrorPage
                    error={new Error(`Unknown error loading brief: ${error.message}`)}
                    metadata={{ briefId }}
                />
            );
        }

        // Ignore 'no product found for mpvID' as mpvID can be empty or incorrect without causing an actual error.
        // ToDo: Remove this check once confident the error no longer occurs.
        if (!error.message.includes('no product found for mpvID')) {
            return <ErrorPage error={error} />;
        }
    }

    return (
        <>
            <Helmet>
                <title>{__('Design Services Brief')} | Vistaprint</title>
            </Helmet>
            <MultiStepBriefForm brief={data.brief} locale={locale} />
        </>
    );
};
